if (!customElements.get('progress-chart')) {
  const SVG_NS = 'http://www.w3.org/2000/svg';
  /** Fallbacks when `data-chart-*-ms` attrs are missing (see performance-journey section). */
  const DEFAULT_LINE_DURATION_MS = 2100;
  const DEFAULT_LOOP_GAP_MS = 5000;
  const DEFAULT_POINT_PAUSE_MS = 1200;

  class ProgressChart extends HTMLElement {
    connectedCallback() {
      this.init();
      if (Shopify.designMode) {
        this._onSectionLoad = this.handleSectionLoad.bind(this);
        document.addEventListener('shopify:section:load', this._onSectionLoad);
      }
    }

    disconnectedCallback() {
      this.clearChartLoop();
      this.cancelChartAnimations();
      if (this._observer) this._observer.disconnect();
      if (this._fillRAF) cancelAnimationFrame(this._fillRAF);
      if (this._resizeObserver) this._resizeObserver.disconnect();
      if (this._docTouchHandler) {
        document.removeEventListener('touchstart', this._docTouchHandler);
      }
      if (Shopify.designMode && this._onSectionLoad) {
        document.removeEventListener('shopify:section:load', this._onSectionLoad);
      }
    }

    handleSectionLoad(event) {
      const sectionEl = this.closest('.shopify-section');
      if (!sectionEl || event.detail.sectionId !== sectionEl.id.replace('shopify-section-', '')) return;
      this.removeAttribute('data-animated');
      this.clearChartLoop();
      this.cancelChartAnimations();
      if (this._observer) this._observer.disconnect();
      if (this._fillRAF) cancelAnimationFrame(this._fillRAF);
      this.init();
    }

    /**
     * Reads animation timing from `data-chart-line-duration-ms`, `data-chart-loop-gap-ms`,
     * `data-chart-point-pause-ms` (section settings → dataset).
     */
    applyTimingFromDataset() {
      const parseMs = (val, fallback) => {
        if (val === undefined || val === '') return fallback;
        const n = parseInt(String(val), 10);
        return Number.isFinite(n) && n >= 0 ? n : fallback;
      };
      this.lineDurationMs = parseMs(this.dataset.chartLineDurationMs, DEFAULT_LINE_DURATION_MS);
      this.loopGapMs = parseMs(this.dataset.chartLoopGapMs, DEFAULT_LOOP_GAP_MS);
      this.pointPauseMs = parseMs(this.dataset.chartPointPauseMs, DEFAULT_POINT_PAUSE_MS);
      // data-chart-animation-loop="true"|"false" — default on when missing (older markup).
      this.animationLoopEnabled = this.dataset.chartAnimationLoop !== 'false';
    }

    init() {
      this.clearChartLoop();
      this.applyTimingFromDataset();
      const configScript = this.querySelector('[data-chart-config]');
      if (!configScript) return;

      try {
        this.config = JSON.parse(configScript.textContent);
      } catch (e) {
        return;
      }

      this.config.points = (this.config.points || []).filter(Boolean);
      if (this.config.points.length < 2) return;

      this.config.points.sort((a, b) => a.x - b.x);

      const existing = this.querySelector('svg');
      if (existing) existing.remove();
      this.querySelectorAll('.progress-chart__badge, .progress-chart__tooltip').forEach(el => el.remove());

      if (this._resizeObserver) this._resizeObserver.disconnect();
      if (this._docTouchHandler) {
        document.removeEventListener('touchstart', this._docTouchHandler);
        this._docTouchHandler = null;
      }

      this.viewBox = { w: 800, h: 400 };
      this.padding = { top: 40, right: 30, bottom: 90, left: 120 };
      this.chartArea = {
        x: this.padding.left,
        y: this.padding.top,
        w: this.viewBox.w - this.padding.left - this.padding.right,
        h: this.viewBox.h - this.padding.top - this.padding.bottom,
      };

      this.renderChart();
      this.renderBadges();
      this.setupTooltips();
      this.setupAnimation();
    }

    toSvgCoords(point) {
      return {
        x: this.chartArea.x + (point.x / 100) * this.chartArea.w,
        y: this.chartArea.y + ((100 - point.y) / 100) * this.chartArea.h,
      };
    }

    svgEl(tag, attrs) {
      const el = document.createElementNS(SVG_NS, tag);
      for (const [k, v] of Object.entries(attrs || {})) {
        el.setAttribute(k, v);
      }
      return el;
    }

    renderChart() {
      const svg = this.svgEl('svg', {
        viewBox: `0 0 ${this.viewBox.w} ${this.viewBox.h}`,
        'aria-label': 'Progress chart',
        role: 'img',
      });

      const defs = this.svgEl('defs');
      const sectionEl = this.closest('.shopify-section');
      const uniqueId = sectionEl ? sectionEl.id.replace('shopify-section-', '') : 'default';
      const gradId = 'chartGradient-' + uniqueId;
      const clipId = 'chartClip-' + uniqueId;

      const grad = this.svgEl('linearGradient', { id: gradId, x1: '0', y1: '0', x2: '1', y2: '0' });
      grad.appendChild(this.svgEl('stop', { offset: '0%', 'stop-color': 'var(--chart-color, #2563eb)', 'stop-opacity': '0.18' }));
      grad.appendChild(this.svgEl('stop', { offset: '100%', 'stop-color': 'var(--chart-color, #2563eb)', 'stop-opacity': '0.03' }));
      defs.appendChild(grad);

      const clipPath = this.svgEl('clipPath', { id: clipId });
      this._clipRect = this.svgEl('rect', {
        x: this.chartArea.x,
        y: 0,
        width: this.chartArea.w + this.padding.right,
        height: this.viewBox.h,
      });
      clipPath.appendChild(this._clipRect);
      defs.appendChild(clipPath);

      svg.appendChild(defs);

      const yLabels = this.config.yLabels || {};
      const gridPositions = [
        { y: this.chartArea.y, label: yLabels.top || 'PEAK' },
        { y: this.chartArea.y + this.chartArea.h * 0.45, label: yLabels.mid || 'AVERAGE' },
        { y: this.chartArea.y + this.chartArea.h, label: yLabels.bottom || 'POOR' },
      ];

      gridPositions.forEach((pos, idx) => {
        svg.appendChild(this.svgEl('line', {
          x1: this.chartArea.x, y1: pos.y,
          x2: this.chartArea.x + this.chartArea.w, y2: pos.y,
          class: 'progress-chart__grid-line',
        }));
        const labelClass = idx === 0
          ? 'progress-chart__y-label progress-chart__y-label--top'
          : 'progress-chart__y-label';
        const text = this.svgEl('text', {
          x: this.chartArea.x - 28, y: pos.y + 4,
          class: labelClass,
          'text-anchor': 'end',
        });
        const lines = pos.label.split(' ');
        if (lines.length > 1) {
          lines.forEach((line, i) => {
            const tspan = this.svgEl('tspan', {
              x: this.chartArea.x - 28,
              dy: i === 0 ? `-${(lines.length - 1) * 0.55}em` : '1.1em',
            });
            tspan.textContent = line;
            text.appendChild(tspan);
          });
        } else {
          text.textContent = pos.label;
        }
        svg.appendChild(text);
      });

      svg.appendChild(this.svgEl('line', {
        x1: this.chartArea.x,
        y1: this.chartArea.y - 25,
        x2: this.chartArea.x,
        y2: this.chartArea.y + this.chartArea.h + 20,
        class: 'progress-chart__axis-line',
      }));

      svg.appendChild(this.svgEl('line', {
        x1: this.chartArea.x - 1,
        y1: this.chartArea.y + this.chartArea.h,
        x2: this.chartArea.x + this.chartArea.w,
        y2: this.chartArea.y + this.chartArea.h,
        class: 'progress-chart__axis-line',
      }));

      const svgPoints = this.config.points.map(p => this.toSvgCoords(p));
      const curvePath = this.generateSmoothPath(svgPoints);
      const bottomY = this.chartArea.y + this.chartArea.h;

      const clippedGroup = this.svgEl('g', { 'clip-path': `url(#${clipId})` });

      const fillD = `${curvePath} L ${svgPoints[svgPoints.length - 1].x},${bottomY} L ${svgPoints[0].x},${bottomY} Z`;
      clippedGroup.appendChild(this.svgEl('path', { d: fillD, fill: `url(#${gradId})`, class: 'progress-chart__fill' }));

      this._dashedLineEls = new Array(this.config.points.length).fill(null);
      this.config.points.forEach((point, i) => {
        if (point.dashedLine) {
          const sp = svgPoints[i];
          const dl = this.svgEl('line', {
            x1: sp.x, y1: sp.y,
            x2: sp.x, y2: bottomY,
            class: 'progress-chart__dashed-line',
            style: 'opacity: 0',
          });
          clippedGroup.appendChild(dl);
          this._dashedLineEls[i] = dl;
        }
      });

      svg.appendChild(clippedGroup);

      this._curvePath = this.svgEl('path', { d: curvePath, class: 'progress-chart__curve' });
      svg.appendChild(this._curvePath);

      this._pointElements = [];
      this.config.points.forEach((point, i) => {
        const sp = svgPoints[i];
        const isFirst = i === 0;
        const isLast = i === svgPoints.length - 1;

        const g = this.svgEl('g', {
          class: 'progress-chart__point-group',
          'data-index': i,
        });

        const glowOuterR = isLast ? 28 : 22;
        const glowInnerR = isLast ? 18 : 14;
        const goalClass = isLast ? ' progress-chart__glow-ring--goal' : '';

        const glowOuter = this.svgEl('circle', {
          cx: sp.x, cy: sp.y, r: glowOuterR,
          class: `progress-chart__glow-ring progress-chart__glow-ring--outer${goalClass}`,
        });
        const glowInner = this.svgEl('circle', {
          cx: sp.x, cy: sp.y, r: glowInnerR,
          class: `progress-chart__glow-ring${goalClass}`,
        });
        g.appendChild(glowOuter);
        g.appendChild(glowInner);

        if (isLast) {
          g.appendChild(this.svgEl('circle', {
            cx: sp.x, cy: sp.y, r: 10,
            class: 'progress-chart__point-ring',
          }));
        }

        g.appendChild(this.svgEl('circle', {
          cx: sp.x, cy: sp.y,
          r: isLast ? 7 : 6,
          class: isFirst ? 'progress-chart__point progress-chart__point--first' : 'progress-chart__point',
        }));
        g.appendChild(this.svgEl('circle', {
          cx: sp.x, cy: sp.y, r: 22,
          class: 'progress-chart__point-hit',
        }));
        svg.appendChild(g);

        const pointData = { group: g, glowInner, glowOuter, xLabel: null, xSublabel: null, badge: null };

        if (point.label) {
          const labelText = this.svgEl('text', {
            x: sp.x, y: bottomY + 60,
            class: 'progress-chart__x-label',
            'text-anchor': 'middle',
          });
          labelText.textContent = point.label;
          svg.appendChild(labelText);
          pointData.xLabel = labelText;
        }

        if (point.sublabel) {
          const subText = this.svgEl('text', {
            x: sp.x, y: bottomY + 90,
            class: 'progress-chart__x-sublabel',
            'text-anchor': 'middle',
          });
          subText.textContent = point.sublabel;
          svg.appendChild(subText);
          pointData.xSublabel = subText;
        }

        this._pointElements.push(pointData);
      });

      this.prepend(svg);
      this._svg = svg;
      this._svgPoints = svgPoints;
    }

    generateSmoothPath(points) {
      if (points.length < 2) return '';
      if (points.length === 2) {
        return `M ${points[0].x},${points[0].y} L ${points[1].x},${points[1].y}`;
      }

      let d = `M ${points[0].x},${points[0].y}`;
      const n = points.length;

      for (let i = 0; i < n - 1; i++) {
        const p0 = points[Math.max(i - 1, 0)];
        const p1 = points[i];
        const p2 = points[Math.min(i + 1, n - 1)];
        const p3 = points[Math.min(i + 2, n - 1)];

        const tension = 0.35;
        const cp1x = p1.x + (p2.x - p0.x) * tension;
        const cp1y = p1.y + (p2.y - p0.y) * tension;
        const cp2x = p2.x - (p3.x - p1.x) * tension;
        const cp2y = p2.y - (p3.y - p1.y) * tension;

        d += ` C ${cp1x},${cp1y} ${cp2x},${cp2y} ${p2.x},${p2.y}`;
      }

      return d;
    }

    /**
     * Find distance along path (from start) closest to (x, y), searching [minLen, maxLen].
     */
    findPathLengthForPoint(pathEl, x, y, minLen, maxLen) {
      const steps = 64;
      let bestD = minLen;
      let bestDist = Infinity;
      for (let s = 0; s <= steps; s++) {
        const d = minLen + ((maxLen - minLen) * s) / steps;
        const p = pathEl.getPointAtLength(d);
        const dist = (p.x - x) ** 2 + (p.y - y) ** 2;
        if (dist < bestDist) {
          bestDist = dist;
          bestD = d;
        }
      }
      return bestD;
    }

    /**
     * Cumulative path length at each chart point (monotonic), for segmented stroke animation.
     */
    computeLengthAtPoints() {
      const path = this._curvePath;
      const pts = this._svgPoints;
      if (!path || !pts || pts.length === 0) return [];
      const total = path.getTotalLength();
      const lengths = [];
      lengths[0] = 0;
      let prev = 0;
      for (let i = 1; i < pts.length; i++) {
        const lo = prev;
        const hi = total;
        let len = this.findPathLengthForPoint(path, pts[i].x, pts[i].y, lo, hi);
        len = Math.max(lo, Math.min(len, total));
        lengths.push(len);
        prev = len;
      }
      lengths[pts.length - 1] = total;
      for (let i = 1; i < lengths.length; i++) {
        if (lengths[i] < lengths[i - 1]) lengths[i] = lengths[i - 1];
      }
      return lengths;
    }

    renderBadges() {
      if (!this._svg || !this._svgPoints) return;

      this.config.points.forEach((point, i) => {
        if (!point.badge || !point.badge.trim()) return;
        const sp = this._svgPoints[i];

        const badge = document.createElement('div');
        badge.className = `progress-chart__badge progress-chart__badge--${point.badgeStyle || 'dark'}`;
        badge.textContent = point.badge;
        badge.dataset.pointIndex = i;
        this.appendChild(badge);

        this._positionBadge(badge, sp, i);

        if (this._pointElements && this._pointElements[i]) {
          this._pointElements[i].badge = badge;
        }
      });
    }

    _positionBadge(badge, svgPoint, index) {
      requestAnimationFrame(() => {
        const svgRect = this._svg.getBoundingClientRect();
        const containerRect = this.getBoundingClientRect();
        const scaleX = svgRect.width / this.viewBox.w;
        const scaleY = svgRect.height / this.viewBox.h;

        const domX = svgRect.left - containerRect.left + svgPoint.x * scaleX;
        const domY = svgRect.top - containerRect.top + svgPoint.y * scaleY;

        const bw = badge.offsetWidth;
        const bh = badge.offsetHeight;
        const isFirst = index === 0;
        const isLast = index === this.config.points.length - 1;

        if (isLast) {
          badge.style.left = `${domX - bw / 2}px`;
          badge.style.top = `${domY - bh - 14}px`;
        } else if (isFirst) {
          const pointRadius = 6 * scaleX;
          badge.style.left = `${domX + pointRadius + 5}px`;
          badge.style.top = `${domY - bh / 2}px`;
        } else {
          badge.style.left = `${domX - bw / 2}px`;
          badge.style.top = `${domY + 12}px`;
        }
      });
    }

    setupTooltips() {
      let tooltip = this.querySelector('.progress-chart__tooltip');
      if (!tooltip) {
        tooltip = document.createElement('div');
        tooltip.className = 'progress-chart__tooltip';
        this.appendChild(tooltip);
      }
      this._tooltip = tooltip;

      const pointGroups = this._svg.querySelectorAll('.progress-chart__point-group');
      pointGroups.forEach(g => {
        const idx = parseInt(g.dataset.index, 10);
        const point = this.config.points[idx];
        if (!point || !point.tooltip) return;

        g.addEventListener('mouseenter', () => {
          if (this._playbackActive) return;
          this.showTooltip(idx);
        });
        g.addEventListener('mouseleave', () => {
          if (this._playbackActive) return;
          this.hideTooltip();
        });
        g.addEventListener('touchstart', (e) => {
          if (this._playbackActive) return;
          e.preventDefault();
          this.showTooltip(idx);
        }, { passive: false });
      });

      this._docTouchHandler = (e) => {
        if (this._playbackActive) return;
        if (!this.contains(e.target)) this.hideTooltip();
      };
      document.addEventListener('touchstart', this._docTouchHandler, { passive: true });

      this._resizeObserver = new ResizeObserver(() => {
        this.querySelectorAll('.progress-chart__badge').forEach(badge => {
          const idx = parseInt(badge.dataset.pointIndex, 10);
          this._positionBadge(badge, this._svgPoints[idx], idx);
        });
      });
      this._resizeObserver.observe(this);
    }

    showTooltip(index) {
      const point = this.config.points[index];
      const sp = this._svgPoints[index];
      if (!point.tooltip || !this._tooltip || !this._svg) return;

      this._tooltip.textContent = point.tooltip;
      this._tooltip.classList.add('progress-chart__tooltip--visible');

      const svgRect = this._svg.getBoundingClientRect();
      const containerRect = this.getBoundingClientRect();
      const scaleX = svgRect.width / this.viewBox.w;
      const scaleY = svgRect.height / this.viewBox.h;

      const domX = svgRect.left - containerRect.left + sp.x * scaleX;
      const domY = svgRect.top - containerRect.top + sp.y * scaleY;

      const tw = this._tooltip.offsetWidth;
      let left = domX - tw / 2;
      left = Math.max(0, Math.min(left, containerRect.width - tw));

      this._tooltip.style.left = `${left}px`;
      this._tooltip.style.top = `${domY - this._tooltip.offsetHeight - 16}px`;
    }

    hideTooltip() {
      if (this._tooltip) {
        this._tooltip.classList.remove('progress-chart__tooltip--visible');
      }
    }

    /**
     * Clears WAAPI fill:forwards / pulse / inline styles on point graphics so the next
     * cycle does not inherit visible dots or glow from the previous run.
     */
    clearPointPresentationStyles() {
      if (!this._pointElements) return;
      this._pointElements.forEach((pe) => {
        pe.group.style.opacity = '0';

        if (pe.glowInner) {
          pe.glowInner.style.removeProperty('opacity');
          pe.glowInner.style.removeProperty('transform');
        }
        if (pe.glowOuter) {
          pe.glowOuter.style.removeProperty('opacity');
          pe.glowOuter.style.removeProperty('transform');
        }

        const dot = pe.group.querySelector('.progress-chart__point');
        if (dot) {
          dot.style.removeProperty('opacity');
          dot.style.removeProperty('transform');
        }

        const ring = pe.group.querySelector('.progress-chart__point-ring');
        if (ring) {
          ring.style.removeProperty('opacity');
          ring.style.removeProperty('transform');
        }

        if (pe.xLabel) pe.xLabel.style.opacity = '0';
        if (pe.xSublabel) pe.xSublabel.style.opacity = '0';
        if (pe.badge) {
          pe.badge.style.opacity = '0';
          pe.badge.style.removeProperty('transform');
        }
      });
    }

    hideAnimatableElements() {
      if (!this._curvePath || !this._clipRect) return;
      this._curvePath.style.opacity = '0';
      this._clipRect.setAttribute('width', '0');
      this.clearPointPresentationStyles();
    }

    clearChartLoop() {
      if (this._loopTimeoutId != null) {
        clearTimeout(this._loopTimeoutId);
        this._loopTimeoutId = null;
      }
    }

    cancelChartAnimations() {
      if (this._seqSleepTimer) {
        clearTimeout(this._seqSleepTimer);
        this._seqSleepTimer = null;
      }
      if (this._fillRAF) {
        cancelAnimationFrame(this._fillRAF);
        this._fillRAF = null;
      }
      if (typeof this.getAnimations === 'function') {
        this.getAnimations({ subtree: true }).forEach((anim) => {
          try {
            anim.cancel();
          } catch (e) {
            /* ignore */
          }
        });
      }
    }

    resetIntroState() {
      this.hideTooltip();
      this.hideAnimatableElements();
      if (this._curvePath) {
        this._curvePath.style.strokeDasharray = '';
        this._curvePath.style.strokeDashoffset = '';
      }
      if (this._dashedLineEls) {
        this._dashedLineEls.forEach((el) => {
          if (el) el.style.opacity = '0';
        });
      }
    }

    /**
     * Approximate wall-clock duration (ms) of one sequential intro (stroke + pauses + point keyframes).
     * Loop timing uses Promise completion + loopGapMs; this is for estimates/debug only.
     */
    computeIntroCompleteMs() {
      const n = this.config.points.length;
      const pointKeyframeBuffer = 900;
      return this.lineDurationMs + n * this.pointPauseMs + n * pointKeyframeBuffer;
    }

    sleep(ms) {
      return new Promise((resolve) => {
        if (this._seqSleepTimer) clearTimeout(this._seqSleepTimer);
        this._seqSleepTimer = setTimeout(() => {
          this._seqSleepTimer = null;
          resolve();
        }, ms);
      });
    }

    animateStrokeSegment(fromLen, toLen, durationMs, introGen) {
      return new Promise((resolve) => {
        if (introGen !== this._introGeneration) {
          resolve();
          return;
        }
        const curve = this._curvePath;
        const total = curve.getTotalLength();
        curve.style.strokeDasharray = String(total);
        const startOff = total - fromLen;
        const endOff = total - toLen;
        curve.style.strokeDashoffset = String(startOff);

        if (durationMs <= 0 || Math.abs(toLen - fromLen) < 0.25) {
          curve.style.strokeDashoffset = String(endOff);
          resolve();
          return;
        }

        const anim = curve.animate(
          [{ strokeDashoffset: startOff }, { strokeDashoffset: endOff }],
          { duration: durationMs, easing: 'cubic-bezier(0.25, 0.1, 0.25, 1)', fill: 'forwards' }
        );
        anim.onfinish = () => resolve();
        anim.oncancel = () => resolve();
      });
    }

    async runSequentialIntro(introGen) {
      this._playbackActive = true;
      const lengths = this._lengthAtPoint;
      const n = this.config.points.length;
      const total = this._curvePath.getTotalLength();
      const fullWidth = this.chartArea.w + this.padding.right;
      const pathTotal = lengths && lengths.length ? lengths[n - 1] : total;

      this._curvePath.style.opacity = '1';
      this._curvePath.style.strokeDasharray = String(total);
      this._curvePath.style.strokeDashoffset = String(total);

      if (this._dashedLineEls) {
        this._dashedLineEls.forEach((el) => {
          if (el) el.style.opacity = '0';
        });
      }

      try {
        for (let i = 0; i < n; i++) {
          if (introGen !== this._introGeneration) return;

          if (i > 0) {
            const fromLen = lengths[i - 1];
            const toLen = lengths[i];
            const segLen = Math.max(0, toLen - fromLen);
            const segDur = pathTotal > 0 ? this.lineDurationMs * (segLen / pathTotal) : 0;
            await this.animateStrokeSegment(fromLen, toLen, segDur, introGen);
            if (introGen !== this._introGeneration) return;
          } else {
            this._curvePath.style.strokeDashoffset = String(total - lengths[0]);
          }

          const clipW = Math.min(fullWidth, Math.max(0, this._svgPoints[i].x - this.chartArea.x));
          this._clipRect.setAttribute('width', String(clipW));

          if (this._dashedLineEls && this._dashedLineEls[i]) {
            this._dashedLineEls[i].style.opacity = '1';
          }

          const isLast = i === n - 1;
          this.animatePointIn(i, 0, introGen, { enableGlowPulse: isLast });

          if (this.config.points[i].tooltip) {
            this.showTooltip(i);
          }

          await this.sleep(this.pointPauseMs);
          if (introGen !== this._introGeneration) return;
          this.hideTooltip();
        }
      } finally {
        this._playbackActive = false;
      }
    }

    setupAnimation() {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      if (!('IntersectionObserver' in window)) return;

      if (this._observer) this._observer.disconnect();

      this.hideAnimatableElements();

      this._chartVisible = false;
      this._observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            // Fully outside viewport: stop loop and hide (needs threshold 0 in list to reliably fire).
            if (!entry.isIntersecting) {
              this._chartVisible = false;
              this.clearChartLoop();
              this.cancelChartAnimations();
              this.resetIntroState();
              return;
            }

            this._chartVisible = true;

            // (Re)start intro only when enough of the chart is visible; below this, keep running if already playing.
            if (entry.intersectionRatio >= 0.85) {
              this.clearChartLoop();
              this.cancelChartAnimations();
              this.resetIntroState();
              this.playAnimation();
            }
          });
        },
        // 0: detect last pixel leaving / entering; 0.85: match previous "start when mostly visible" behavior.
        { threshold: [0, 0.85] }
      );
      this._observer.observe(this);
    }

    playAnimation() {
      this.clearChartLoop();

      this._introGeneration = (this._introGeneration || 0) + 1;
      const introGen = this._introGeneration;

      this.cancelChartAnimations();

      requestAnimationFrame(() => {
        if (introGen !== this._introGeneration) return;

        this.resetIntroState();

        this._lengthAtPoint = this.computeLengthAtPoints();

        this.dataset.animated = '';

        this.runSequentialIntro(introGen)
          .then(() => {
            if (introGen !== this._introGeneration) return;
            if (!this._chartVisible) return;
            if (!this.animationLoopEnabled) return;
            this._loopTimeoutId = setTimeout(() => {
              this._loopTimeoutId = null;
              if (!this._chartVisible) return;
              this.cancelChartAnimations();
              this.resetIntroState();
              this.playAnimation();
            }, this.loopGapMs);
          })
          .catch(() => {});
      });
    }

    animatePointIn(index, delay, introGeneration, options = {}) {
      const enableGlowPulse = options.enableGlowPulse === true;
      const pe = this._pointElements[index];
      if (!pe) return;

      const isLast = index === this.config.points.length - 1;

      pe.group.animate(
        [{ opacity: 0 }, { opacity: 1 }],
        { duration: 300, delay, fill: 'both', easing: 'ease-out' }
      );

      const dot = pe.group.querySelector('.progress-chart__point');
      if (dot) {
        dot.animate(
          [
            { transform: 'scale(0)' },
            { transform: 'scale(1.4)', offset: 0.55 },
            { transform: 'scale(1)' },
          ],
          { duration: 500, delay, fill: 'both', easing: 'ease-out' }
        );
      }

      if (isLast) {
        const ring = pe.group.querySelector('.progress-chart__point-ring');
        if (ring) {
          ring.animate(
            [
              { transform: 'scale(0)' },
              { transform: 'scale(1.25)', offset: 0.55 },
              { transform: 'scale(1)' },
            ],
            { duration: 500, delay: delay + 50, fill: 'both', easing: 'ease-out' }
          );
        }
      }

      const glowDelay = delay + 100;
      const innerFinal = isLast ? 0.3 : 0.2;
      const outerFinal = isLast ? 0.15 : 0.08;

      const innerAnim = pe.glowInner.animate(
        [
          { opacity: 0, transform: 'scale(0)' },
          { opacity: 0.5, transform: 'scale(1.3)', offset: 0.45 },
          { opacity: innerFinal, transform: 'scale(1)' },
        ],
        { duration: 600, delay: glowDelay, fill: 'both', easing: 'ease-out' }
      );

      const outerAnim = pe.glowOuter.animate(
        [
          { opacity: 0, transform: 'scale(0)' },
          { opacity: 0.25, transform: 'scale(1.2)', offset: 0.45 },
          { opacity: outerFinal, transform: 'scale(1)' },
        ],
        { duration: 700, delay: glowDelay + 80, fill: 'both', easing: 'ease-out' }
      );

      innerAnim.onfinish = () => {
        if (introGeneration !== this._introGeneration) return;
        pe.glowInner.style.opacity = String(innerFinal);
        pe.glowInner.style.transform = 'scale(1)';
        innerAnim.cancel();
        if (enableGlowPulse) {
          this.startGlowPulse(pe.glowInner, {
            opMin: innerFinal * 0.7, opMax: innerFinal * 1.8,
            scMin: 1, scMax: isLast ? 1.35 : 1.25,
            dur: isLast ? 1400 : 1800,
          });
        }
      };

      outerAnim.onfinish = () => {
        if (introGeneration !== this._introGeneration) return;
        pe.glowOuter.style.opacity = String(outerFinal);
        pe.glowOuter.style.transform = 'scale(1)';
        outerAnim.cancel();
        if (enableGlowPulse) {
          this.startGlowPulse(pe.glowOuter, {
            opMin: outerFinal * 0.6, opMax: outerFinal * 2,
            scMin: 1, scMax: isLast ? 1.2 : 1.15,
            dur: isLast ? 1800 : 2200,
          });
        }
      };

      if (pe.xLabel) {
        pe.xLabel.animate(
          [{ opacity: 0 }, { opacity: 1 }],
          { duration: 350, delay: delay + 150, fill: 'both', easing: 'ease-out' }
        );
      }

      if (pe.xSublabel) {
        pe.xSublabel.animate(
          [{ opacity: 0 }, { opacity: 1 }],
          { duration: 350, delay: delay + 200, fill: 'both', easing: 'ease-out' }
        );
      }

      if (pe.badge) {
        pe.badge.animate(
          [
            { opacity: 0, transform: 'scale(0.85)' },
            { opacity: 1, transform: 'scale(1)' },
          ],
          { duration: 400, delay: delay + 300, fill: 'both', easing: 'ease-out' }
        );
      }
    }

    startGlowPulse(el, cfg) {
      el.animate(
        [
          { opacity: cfg.opMin, transform: `scale(${cfg.scMin})` },
          { opacity: cfg.opMax, transform: `scale(${cfg.scMax})` },
        ],
        { duration: cfg.dur, easing: 'ease-in-out', direction: 'alternate', iterations: Infinity }
      );
    }
  }

  customElements.define('progress-chart', ProgressChart);
}
